import { QUERIES, WithChildren, WITPaginationState } from '@/_metronic/helpers'
import { BaseResponse } from '@/_metronic/helpers/crud-helper/base-list-response'
import { createContext, FC, useContext, useEffect, useMemo, useState } from 'react'
import { useQuery } from 'react-query'
import { FilterReportSalesBudget, initialFilter } from '../../dashboard-forecast/core/_models'
import { getReportSalesBudget } from '../../dashboard-forecast/core/_requests'
import { getRevenueActualNewOutlet } from './_requests'
import { RevenueActualNewOutlet } from './_models'
import { useQueryRequestMonthly } from '../../dashboard-forecast/core/QueryRequestMonthlyProvider'

/**
 * Begin: Initial Values
 */
export const initialQueryResponseNewOutlet = {refetch: () => {}, isLoading: false, request: initialFilter}

/**
 * Begin: Response Context
 *
 * It used for context response wrapper, so the response can
 * called everywhere, as long as it wrapped inside the context
 */
type ProductQueryResponseNewOutletContextProps = {
  response?: BaseResponse<RevenueActualNewOutlet[]> | undefined
  refetch: () => void
  isLoading: boolean
  request: FilterReportSalesBudget
}

function createResponseContext<T>(initialState: ProductQueryResponseNewOutletContextProps) {
  return createContext(initialState)
}

const QueryResponseNewOutletContext = createResponseContext<RevenueActualNewOutlet[]>(initialQueryResponseNewOutlet)

/**
 * Begin: The Provider
 */
const QueryResponseNewOutletProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequestMonthly()
  const [request, setRequest] = useState<FilterReportSalesBudget>(state)
  const updatedQuery = useMemo(() => state, [state])

  useEffect(() => {
    if (request !== updatedQuery) {
      setRequest(updatedQuery)
    }
  }, [updatedQuery])

  const {
    isFetching,
    refetch,
    data: response,
  } = useQuery(
    `${QUERIES.REPORT_ACTUAL_NEW_OUTLET}-${JSON.stringify(request)}`,
    () => {
      return getRevenueActualNewOutlet({year: Number(request.year)})
    },
    {cacheTime: 0, keepPreviousData: true, refetchOnWindowFocus: false}
  )

  return (
    <QueryResponseNewOutletContext.Provider value={{isLoading: isFetching, refetch, response, request}}>
      {children}
    </QueryResponseNewOutletContext.Provider>
  )
}

const useQueryResponseNewOutlet = () => useContext(QueryResponseNewOutletContext)

const useQueryResponseNewOutletData = () => {
  const {response} = useQueryResponseNewOutlet()
  if (!response) {
    return null
  }

  return response?.response?.data || null
}

const useQueryResponseNewOutletPagination = () => {
  const defaultPaginationState: WITPaginationState = {
    page: 1,
    pageSize: 10,
    totalCount: 1,
  }

  const {response} = useQueryResponseNewOutlet()
  if (!response || !response.response) {
    return defaultPaginationState
  }
  const res = response.response
  return {
    page: res.current_page,
    pageSize: res.limit,
    totalCount: res.total_data,
  } as WITPaginationState
}

const useQueryResponseNewOutletLoading = (): boolean => {
  const {isLoading} = useQueryResponseNewOutlet()
  return isLoading
}

export {
  QueryResponseNewOutletProvider,
  useQueryResponseNewOutlet,
  useQueryResponseNewOutletData,
  useQueryResponseNewOutletLoading,
  useQueryResponseNewOutletPagination
}

